import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const BrandContext = createContext({
    brands: [],
    accessible: [],
    current: "hoshizora",
    setCurrent: () => {},
    loaded: false,
});

export const useBrand = () => useContext(BrandContext);

export function BrandProvider({ children }) {
    const { user } = useAuth();
    const [brands, setBrands] = useState([]);
    const [accessible, setAccessible] = useState([]);
    const [current, setCurrentState] = useState(() => {
        return localStorage.getItem("hoshizora_active_brand") || "hoshizora";
    });
    const [loaded, setLoaded] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/brands");
            setBrands(data.brands || []);
            setAccessible(data.accessible || []);
            const stored = localStorage.getItem("hoshizora_active_brand");
            if (stored && (data.accessible || []).includes(stored)) {
                setCurrentState(stored);
            } else {
                setCurrentState(data.current);
                localStorage.setItem("hoshizora_active_brand", data.current);
            }
        } catch {
            // Not logged in — ignore
        } finally {
            setLoaded(true);
        }
    }, [user]);

    useEffect(() => {
        if (user && user.id) refresh();
    }, [user, refresh]);

    // Mirror the active brand onto the <html> element so CSS variables (and
    // all `sage-*` tailwind tokens that read from them) switch instantly
    // when the user flips between brands. See index.css.
    useEffect(() => {
        const el = document.documentElement;
        el.setAttribute("data-brand", current || "hoshizora");
    }, [current]);

    const setCurrent = useCallback((brandId) => {
        if (!accessible.includes(brandId)) return;
        if (window.__unsavedCategoryOrder && !window.confirm(window.__unsavedCategoryOrder)) return;
        window.__unsavedCategoryOrder = null;
        setCurrentState(brandId);
        localStorage.setItem("hoshizora_active_brand", brandId);
    }, [accessible]);

    return (
        <BrandContext.Provider value={{ brands, accessible, current, setCurrent, loaded }}>
            {children}
        </BrandContext.Provider>
    );
}

export const BRAND_META = {
    hoshizora: {
        name: "Hoshizora",
        tagline: "Quiet luxury jewelry.",
        accent: "#6F7D69",
        bg: "#F8F6F2",
        serif: '"Cormorant Garamond", serif',
    },
    heelsandtea: {
        name: "Heels & Tea",
        tagline: "Lifestyle & fashion essentials.",
        accent: "#B47884",
        bg: "#FBF4F2",
        serif: '"Cormorant Garamond", serif',
    },
};
